import React from "react"
import { RequestError } from "../../components/shared/RequestError"
import { FeedSpinner } from "../../components/shared/Spinner"
import Heading from "../../components/shared/Heading"
import { AlbumSearchResult } from "./AlbumSearchResult"
import { ConcertSearchResult } from "./ConcertSearchResult"
import { SongSearchResult } from "./SongSearchResult"
import { HeadlineSearchResult } from "./HeadlineSearchResult"
import VenueSearchResult from "./VenueSearchResult"
import BandSearchResult from "./BandSearchResult"
import { useInfiniteSearchResults } from "../../hooks/useSearchResults"
import type { Band } from "../../types/bands"

interface SearchResultsProps {
  showFollow?: boolean
  cols?: number
  query?: string
}

// Tailwind's JIT compiler only generates CSS for class names it finds as
// literal, complete tokens in source files, so `grid-cols-${cols}` can never
// resolve — the literal strings below must appear verbatim for JIT to pick them up.
const GRID_COLS_CLASSES: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
}

interface SearchBand extends Band {
  cover_image_url?: string
  cover_photo_url_square?: string
}

interface SearchResultEntry {
  type: string
  score: number
  band?: SearchBand
  post?: any
  user?: any
}

interface SearchPage {
  page: number
  total_count: number
  results?: SearchResultEntry[]
  // Legacy sectioned format (when type is not "all")
  bands?: SearchBand[]
  concerts?: any[]
  venues?: SearchBand[]
  albums?: any[]
  songs?: any[]
  blogs?: SearchBand[]
  promoters?: SearchBand[]
  headlines?: any[]
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  showFollow = false,
  cols = 1,
  query = "",
}) => {
  const { data, error, status } = useInfiniteSearchResults({
    query: query,
    pageSize: 12,
  })

  const renderResult = (entry: SearchResultEntry) => {
    const item = entry.band ?? entry.post ?? entry.user
    const key = item?.id ? `${entry.type}-${item.id}` : `result-${Math.random()}`
    if (entry.band) {
      const band = entry.band
      if (entry.type === "venue" || entry.type === "blog" || entry.type === "promoter") {
        return (
          <VenueSearchResult
            key={key}
            bandId={band.id}
            bandName={band.name}
            bandPath={band.band_path || ""}
            bandCoverImageUrl={band.cover_photo_url_square || ""}
            bandLocation={
              band.location ? band.location.displayName || null : null
            }
            bandTypeNice={band.band_type_nice}
          />
        )
      }
      return (
        <BandSearchResult
          key={key}
          bandId={band.id}
          bandName={band.name}
          bandPath={band.band_path || ""}
          bandCoverImageUrl={
            band.cover_image_url || band.cover_photo_url_square || ""
          }
          bandLocation={
            band.location ? band.location.displayName || null : null
          }
          bandTypeNice={band.band_type_nice}
        />
      )
    }
    if (entry.post) {
      const post = entry.post
      switch (entry.type) {
        case "concert":
          return <ConcertSearchResult key={key} concert={post} />
        case "album":
          return <AlbumSearchResult key={key} album={post} />
        case "song":
          return <SongSearchResult key={key} song={post} />
        case "headline":
        case "link":
          return <HeadlineSearchResult key={key} headline={post} />
        default:
          return null
      }
    }
    if (entry.user) {
      const user = entry.user
      return (
        <BandSearchResult
          key={key}
          bandId={user.band?.id ?? user.band_id}
          bandName={user.band?.name ?? user.nickname ?? "User"}
          bandPath={user.band?.band_path || ""}
          bandCoverImageUrl={
            user.band?.cover_image_url || user.band?.cover_photo_url_square || ""
          }
          bandLocation={
            user.band?.location?.displayName ?? null
          }
          bandTypeNice={user.band?.band_type_nice ?? "User"}
        />
      )
    }
    return null
  }

  const pages = data?.pages ?? []
  const hasUnifiedResults = pages.some(
    (p: SearchPage) => p.results && p.results.length > 0
  )
  const totalCount = pages[0]?.total_count ?? 0

  return status === "loading" ? (
    <FeedSpinner />
  ) : status === "error" ? (
    <RequestError error={error} />
  ) : totalCount === 0 ? (
    <>
      <Heading level={2} type="sectionHeader">
        Search Results
      </Heading>
      No results
    </>
  ) : (
    <>
      <Heading level={2} type="sectionHeader">
        Search Results
      </Heading>
      {hasUnifiedResults ? (
        <ul className={`grid ${GRID_COLS_CLASSES[cols] || "grid-cols-1"} gap-8`}>
          {pages.flatMap((page: SearchPage) =>
            (page.results || []).map((entry) => renderResult(entry))
          )}
        </ul>
      ) : (
        pages.map((page: SearchPage) => (
          <React.Fragment key={page.page}>
            {page.bands?.length > 0 && (
              <div className="mb-6">
                <Heading level={3} type="sectionHeader">
                  Bands
                </Heading>
                <ul className={`grid ${GRID_COLS_CLASSES[cols] || "grid-cols-1"} gap-8`}>
                  {page.bands.map((band) => (
                  <BandSearchResult
                    key={band.id}
                    bandId={band.id}
                    bandName={band.name}
                    bandPath={band.band_path || ""}
                    bandCoverImageUrl={
                        band.cover_image_url || band.cover_photo_url_square || ""
                    }
                    bandLocation={
                      band.location ? band.location.displayName || null : null
                    }
                    bandTypeNice={band.band_type_nice}
                  />
                  ))}
                </ul>
              </div>
            )}
            {page.concerts?.map((concert) => (
              <ConcertSearchResult key={concert.id} concert={concert} />
            ))}
            {page.albums?.map((album) => (
              <AlbumSearchResult key={album.id} album={album} />
            ))}
            {page.songs?.map((song) => (
              <SongSearchResult key={song.id} song={song} />
            ))}
            {page.venues?.map((venue) => (
              <VenueSearchResult
                key={venue.id}
                bandId={venue.id}
                bandName={venue.name}
                bandPath={venue.band_path || ""}
                bandCoverImageUrl={venue.cover_photo_url_square || ""}
                bandLocation={
                  venue.location ? venue.location.displayName || null : null
                }
                bandTypeNice={venue.band_type_nice}
              />
            ))}
            {page.blogs?.map((blog) => (
              <VenueSearchResult
                key={blog.id}
                bandId={blog.id}
                bandName={blog.name}
                bandPath={blog.band_path || ""}
                bandCoverImageUrl={blog.cover_photo_url_square || ""}
                bandLocation={
                  blog.location ? blog.location.displayName || null : null
                }
                bandTypeNice={blog.band_type_nice}
              />
            ))}
            {page.promoters?.map((promoter) => (
              <VenueSearchResult
                key={promoter.id}
                bandId={promoter.id}
                bandName={promoter.name}
                bandPath={promoter.band_path || ""}
                bandCoverImageUrl={promoter.cover_photo_url_square || ""}
                bandLocation={
                  promoter.location
                    ? promoter.location.displayName || null
                    : null
                }
                bandTypeNice={promoter.band_type_nice}
              />
            ))}
            {page.headlines?.map((headline) => (
              <HeadlineSearchResult key={headline.id} headline={headline} />
            ))}
          </React.Fragment>
        ))
      )}
    </>
  )
}

export default SearchResults
